import BigNumber from 'bignumber.js'
import { getFullnodeUrl, SuiClient } from '@mysten/sui/client'
import { LiquidationAsset, NetworkConfig, TargetCoinLiquidation } from './types'
import { WalletTokenState } from './walletTokenFetcher'
import { AggregatorClient } from '@cetusprotocol/aggregator-sdk'
import { getRefCode } from '@/utils/localStorage'
import { getPartnerInfoFromJSON } from '@/coinLiquidation/swapUtils'
import BN from 'bn.js'
import { Transaction } from '@mysten/sui/transactions'

export const DEFAULT_SUI_RPC = getFullnodeUrl('mainnet')
export const DEFAULT_SUI_SWAP_API = process.env.REACT_APP_SUI_SWAP_API_BASE || ''
export const DEFAULT_SUI_PRICE_API =
  process.env.REACT_APP_SUI_PRICE_API_BASE || 'https://api-sui.cetus.zone/v3/sui/market_price'

const SUI_CHAIN_ID = 784
export const SUI_COIN_TYPE = '0x2::sui::SUI'
export const ACTUAL_SUI_COIN_TYPE =
  '0x0000000000000000000000000000000000000000000000000000000000000002::sui::SUI'
export const CETUS_COIN_TYPE =
  '0x6864a6f921804860930db6ddbe2e16acdf8504495ea7481637a1c8b9a8fe54b::cetus::CETUS'
export const CETUS_PRICE_COIN_TYPE =
  '0x06864a6f921804860930db6ddbe2e16acdf8504495ea7481637a1c8b9a8fe54b::cetus::CETUS'

const coinTypeCheck = (ctype: string, isReverse: boolean = false) => {
  const special = isReverse
    ? {
        [ACTUAL_SUI_COIN_TYPE]: SUI_COIN_TYPE,
        [CETUS_PRICE_COIN_TYPE]: CETUS_COIN_TYPE,
      }
    : {
        [SUI_COIN_TYPE]: ACTUAL_SUI_COIN_TYPE,
        [CETUS_COIN_TYPE]: CETUS_PRICE_COIN_TYPE,
      }

  if (!special?.[ctype]) return ctype
  return special[ctype]
}
const SUI_DECIMALS = 9

type SuiTokenInfo = {
  coinType: string
  symbol: string
  name: string
  decimals: number
  price?: number
  logoURI?: string
}

let suiTokenListCache: SuiTokenInfo[] | null = null

const getSuiNetworkConfig = (rpcUrl?: string): NetworkConfig => ({
  name: 'Sui',
  rpc: rpcUrl || DEFAULT_SUI_RPC,
  chainId: SUI_CHAIN_ID,
  chainIdHex: '',
  explorer: 'https://suiexplorer.com',
  queryToken: '',
  symbol: 'SUI',
  baseCoin: 'USDC',
  cmcCoinId: 20947,
  extension: '-SUI',
  decimals: SUI_DECIMALS,
  transactionPath: '/txblock',
  logo: '',
  publicRPC: rpcUrl || DEFAULT_SUI_RPC,
  nativeCurrency: {
    name: 'Sui',
    symbol: 'SUI',
    cmcCoinId: 20947,
  },
})

const getSuiClient = (rpcUrl?: string) => new SuiClient({ url: rpcUrl || DEFAULT_SUI_RPC })

const getCetusClient = async (walletAddress?: string) => {
  const refCode = getRefCode()
  const refInfo = getPartnerInfoFromJSON(refCode)
  return new AggregatorClient({
    signer: walletAddress,
    overlayFeeRate: new BigNumber(refInfo.feeBps).div(new BigNumber(10000)).toNumber(),
    overlayFeeReceiver: refInfo.suiWalletAddress,
    partner: '0xab0f14c00009d580613b22a050d7c9b694414cb7cb7800d4138d4a52913e21e7',
  })
}

const normalizeTokenInfo = (token: any): SuiTokenInfo | null => {
  const coinType = token?.coinType || token?.type || token?.address || token?.id
  if (!coinType) {
    return null
  }

  return {
    coinType,
    symbol: token?.symbol || token?.ticker || token?.coin_symbol || token?.name || coinType,
    name: token?.name || token?.coin_name || token?.symbol || coinType,
    decimals: Number(token?.decimals ?? token?.decimal ?? SUI_DECIMALS),
    price: Number(token?.price ?? token?.usdPrice ?? token?.priceUsd ?? token?.usd ?? 0) || 0,
    logoURI: token?.logoURI || token?.icon || token?.logo,
  }
}

const getTokenListFromResponse = (data: any): any[] => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.coins)) return data.coins
  if (Array.isArray(data?.tokens)) return data.tokens
  if (Array.isArray(data?.result)) return data.result
  return []
}

const fetchCetusTokenList = async (
  rpcUrl?: string,
  swapApiUrl?: string,
): Promise<SuiTokenInfo[]> => {
  try {
    const response = await fetch('https://api-sui.cetus.zone/v3/sui/clmm/verified_coins_info')
    const responseJson = await response.json()
    const list = responseJson.data.list
    return list.map(normalizeTokenInfo).filter(Boolean) as SuiTokenInfo[]
  } catch (_) {
    return []
  }
}

export const fetchSuiTokenList = async (
  swapApiUrl?: string,
  rpcUrl?: string,
): Promise<SuiTokenInfo[]> => {
  console.log(swapApiUrl, rpcUrl)
  if (suiTokenListCache) {
    return suiTokenListCache
  }
  const cetusTokens = await fetchCetusTokenList(rpcUrl, swapApiUrl)
  if (cetusTokens.length > 0) {
    suiTokenListCache = cetusTokens
    return cetusTokens
  }
  const base = (swapApiUrl || DEFAULT_SUI_SWAP_API || '').replace(/\/$/, '')
  console.log('base', base)
  if (!base) {
    return []
  }

  const paths = [
    'coins',
    'tokens',
    'tokenlist',
    'token-list',
    'v1/coins',
    'v1/tokens',
    'api/v1/coins',
    'api/v1/tokens',
  ]
  for (const path of paths) {
    try {
      const res = await fetch(`${base}/${path}`)
      if (!res.ok) continue
      const data = await res.json()
      const list = getTokenListFromResponse(data)
      const normalized = list.map(normalizeTokenInfo).filter(Boolean) as SuiTokenInfo[]
      if (normalized.length > 0) {
        suiTokenListCache = normalized
        return normalized
      }
    } catch (_) {}
  }
  return []
}

export const getSuiTokenInfo = async (
  coinType: string,
  rpcUrl?: string,
  swapApiUrl?: string,
): Promise<SuiTokenInfo> => {
  const tokenList = await fetchSuiTokenList(swapApiUrl, rpcUrl)
  const fromList = tokenList.find((token) => token.coinType === coinType)
  if (fromList) {
    return fromList
  }

  const client = getSuiClient(rpcUrl)
  const metadata = await client.getCoinMetadata({ coinType }).catch(() => null)
  if (metadata) {
    return {
      coinType,
      symbol: metadata.symbol || coinType,
      name: metadata.name || metadata.symbol || coinType,
      decimals: metadata.decimals ?? SUI_DECIMALS,
      price: 0,
      logoURI: metadata.iconUrl || undefined,
    }
  }

  return {
    coinType,
    symbol: coinType,
    name: coinType,
    decimals: SUI_DECIMALS,
    price: 0,
  }
}

export const getSuiCoinTypeByTarget = async (
  target: TargetCoinLiquidation,
  swapApiUrl?: string,
  rpcUrl?: string,
): Promise<string | null> => {
  if (target === TargetCoinLiquidation.NATIVE) {
    return SUI_COIN_TYPE
  }
  const tokenList = await fetchSuiTokenList(swapApiUrl, rpcUrl)
  const symbol = target === TargetCoinLiquidation.USDT ? 'USDT' : 'USDC'
  const match = tokenList.find((token) => token.symbol?.toUpperCase() === symbol)
  return match?.coinType || null
}

export const getSuiTokenPrices = async (
  coinTypes: string[],
  swapApiUrl?: string,
  rpcUrl?: string,
  priceApiUrl?: string,
): Promise<Record<string, number>> => {
  const prices: Record<string, number> = {}
  if (coinTypes.length === 0) {
    return prices
  }

  const priceBase = (priceApiUrl || DEFAULT_SUI_PRICE_API || '').replace(/\/$/, '')
  console.log('priceBase', priceBase)
  console.log('coinTypes', coinTypes)
  if (priceBase) {
    try {
      const res = await fetch(
        `${priceBase}?base_symbol_address=${encodeURIComponent(coinTypes.map((e) => coinTypeCheck(e)).join(','))}`,
      )
      if (res.ok) {
        const data = await res.json()
        const entries = data?.data?.prices
        for (const entry of entries) {
          const price = Number(entry?.price)
          if (!Number.isNaN(price)) {
            prices[coinTypeCheck(entry.base_symbol, true)] = price
          }
        }
      }
    } catch (_) {}
  }
  return prices
}

export const fetchSuiWalletTokens = async (
  userAddress: string,
  rpcUrl?: string,
  swapApiUrl?: string,
  onProgress?: (state: WalletTokenState) => void,
): Promise<WalletTokenState> => {
  const client = getSuiClient(rpcUrl)
  const network = getSuiNetworkConfig(rpcUrl)

  const tokenByNetwork: Record<string, LiquidationAsset[]> = {}
  const tokensList: LiquidationAsset[] = []
  let error: string | undefined

  const chainStatuses = [
    {
      chainId: network.chainId,
      chainName: network.name,
      status: 'loading' as any,
      tokenCount: 0,
      error: undefined as any,
    },
  ]

  const updateProgress = (isLoading: boolean) => {
    onProgress?.({
      tokenByNetwork: { ...tokenByNetwork },
      tokensList: [...tokensList],
      isLoading,
      error,
      chainStatuses: [...chainStatuses],
      completedChains: isLoading ? 0 : 1,
      totalChains: 1,
    })
  }

  updateProgress(true)

  try {
    const balances = await client.getAllBalances({ owner: userAddress })
    console.log('balances', balances)
    const coinTypes = balances.map((balance) => balance.coinType)
    const prices = await getSuiTokenPrices(coinTypes)

    const useAbleList = Object.keys(prices)
    const metadataList = await Promise.all(
      useAbleList.map((coinType) => client.getCoinMetadata({ coinType }).catch(() => null)),
    )
    console.log('metadataList', metadataList)
    const metadataMap = new Map<string, any>()
    useAbleList.forEach((coinType, index) => {
      metadataMap.set(coinType, metadataList[index])
    })

    const parsedTokens: LiquidationAsset[] = []

    balances.forEach((balance) => {
      const totalBalance = balance.totalBalance
      if (!totalBalance || totalBalance === '0') {
        return
      }
      if (!useAbleList.includes(balance.coinType)) return
      const metadata = metadataMap.get(balance.coinType)
      const decimals = metadata?.decimals ?? SUI_DECIMALS
      const uiBalance = new BigNumber(totalBalance).dividedBy(10 ** decimals)
      const symbol =
        metadata?.symbol || (balance.coinType === SUI_COIN_TYPE ? 'SUI' : balance.coinType)
      const name = metadata?.name || symbol

      let price = prices[balance.coinType] || 0
      if (!price && (symbol === 'USDC' || symbol === 'USDT')) {
        price = 1
      }

      const balanceString = uiBalance.toFixed()
      const usdValue = price ? Number(balanceString) * price : Number(balanceString)

      parsedTokens.push({
        address: balance.coinType,
        symbol,
        name,
        decimals,
        balance: totalBalance,
        balanceString,
        USDBalance: usdValue,
        stringUSDBalance: usdValue.toString(),
        contract_address: balance.coinType,
        cmcCoinId: 0,
        isCustom: false,
        stringBalance: balanceString,
        network,
        nativeCurrency: network.nativeCurrency || { name: 'Sui', symbol: 'SUI', cmcCoinId: 0 },
        isSelected: false,
        loading: false,
        allocation: 0,
        price: price ? price.toString() : '',
        error: '',
        isNative: balance.coinType === SUI_COIN_TYPE,
      })
    })

    parsedTokens.forEach((token) => tokensList.push(token))
    tokenByNetwork[network.chainId.toString()] = parsedTokens
    chainStatuses[0].status = 'completed'
    chainStatuses[0].tokenCount = parsedTokens.length
  } catch (err: any) {
    error = err?.message || 'Failed to load Sui balances'
    chainStatuses[0].status = 'error'
    chainStatuses[0].error = error
  }

  updateProgress(false)
  return {
    tokenByNetwork,
    tokensList,
    isLoading: false,
    error,
    chainStatuses,
    completedChains: 1,
    totalChains: 1,
  }
}

export const base64ToBytes = (value: string) => {
  if (typeof atob === 'function') {
    const binary = atob(value)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes
  }
  if (typeof Buffer !== 'undefined') {
    return Uint8Array.from(Buffer.from(value, 'base64'))
  }
  throw new Error('No base64 decoder available')
}

const pickCetusRoute = (quote: any) => {
  if (!quote) return null
  if (quote.route) return quote.route
  if (Array.isArray(quote.routes) && quote.routes.length > 0) return quote.routes[0]
  if (Array.isArray(quote?.data?.routes) && quote.data.routes.length > 0)
    return quote.data.routes[0]
  if (Array.isArray(quote?.result?.routes) && quote.result.routes.length > 0) {
    return quote.result.routes[0]
  }
  if (Array.isArray(quote) && quote.length > 0) return quote[0]
  return quote
}

export const getSuiQuote = async ({
  inputCoinType,
  outputCoinType,
  amount,
  slippageBps,
  swapApiUrl,
  rpcUrl,
}: {
  inputCoinType: string
  outputCoinType: string
  amount: string
  slippageBps: number
  swapApiUrl?: string
  rpcUrl?: string
}) => {
  const client: AggregatorClient | void = await getCetusClient().catch(console.log)
  /**
   *       from: tokenIn.contractAddress,
   *       target: tokenOut.contractAddress,
   *       amount: new BN(_amount.toString()),
   *       byAmountIn: true,
   */
  console.log('inputCoinType', inputCoinType)
  const params = {
    from: inputCoinType,
    target: outputCoinType,
    amount: new BN(amount.toString()),
    byAmountIn: true,
  }
  const response = await (client as AggregatorClient).findRouters(params)

  console.log('response', response)
  if (!response) {
    throw new Error('Failed to fetch Sui quote via Cetus SDK')
  }
  return {
    ...response,
    route: pickCetusRoute(response),
    slippageBps,
  }
}

export const getSuiSwapTransaction = async ({
  quote,
  userAddress,
  swapApiUrl,
  rpcUrl,
  slippageBps,
}: {
  quote: any
  userAddress: string
  swapApiUrl?: string
  rpcUrl?: string
  slippageBps?: number
}) => {
  const routes: any[] | null = Array.isArray(quote?.routes)
    ? quote.routes
    : Array.isArray(quote?.routers)
      ? quote.routers
      : quote?.route
        ? [quote.route]
        : null
  if (!routes) {
    throw new Error('Sui swap route not found')
  }
  const effectiveSlippageBps = slippageBps ?? quote?.slippageBps ?? 0
  const byAmountIn = typeof quote?.byAmountIn === 'boolean' ? quote.byAmountIn : true
  console.log('userAddress', userAddress)
  try {
    const client = await getCetusClient(userAddress)
    const txb = new Transaction()

    const routesCheck = routes.map((e) => ({
      ...e,
      path: e.path.map((p) => ({
        ...p,
        target: coinTypeCheck(p.target, true),
        from: coinTypeCheck(p.from, true),
      })),
    }))
    const routersPayload = Array.isArray(quote?.routes)
      ? { ...quote, routes: routesCheck }
      : routesCheck
    console.log('routes', routes)

    const params = {
      routers: routersPayload,
      byAmountIn,
      txb: txb as any,
      refreshAllCoins: true,
      payDeepFeeAmount: 0,
      address: userAddress,
      slippage: effectiveSlippageBps ? effectiveSlippageBps / 10000 : undefined,
    }

    console.log('params', params)

    console.log(txb)
    await client.fastRouterSwap(params as any)

    console.log('sui swap transaction response', txb)
    return txb
  } catch (_) {}

  const base = (swapApiUrl || DEFAULT_SUI_SWAP_API || '').replace(/\/$/, '')
  if (!base) {
    throw new Error('Sui swap API not configured')
  }
  const urls = [`${base}/swap`]
  if (!base.endsWith('/v1')) {
    urls.push(`${base}/v1/swap`)
  }

  for (const url of urls) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        quote,
        userAddress,
      }),
    })
    if (!res.ok) continue
    return res.json()
  }
  throw new Error('Failed to build Sui swap transaction')
}
